import { logger } from '@adornis/base/logging.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { baseConfig } from '@adornis/config/baseConfig.js';
import { AdornisUser } from '@adornis/users/db/a-user.js';
import deTranslations from '../../localization/messages/de.json' with { type: 'json' };
import { sendExamResultsEmail } from '../../server/utils/email.js';
import { Student } from '../student.js';
import { isSubject } from '../subjects.js';

/**
 * The mails are German like every other Gradias mail, so the subject shows with its German UI name. `other` and
 * unknown free text add nothing a student could recognise, so the mail then just leaves the subject out.
 */
function subjectLabelDe(subject: unknown) {
  if (!isSubject(subject) || subject === 'other') return undefined;
  return (deTranslations as { Subjects?: Record<string, string> }).Subjects?.[subject];
}

/**
 * One document per (release, student) that was mailed, `_id` = `${examId}:${releasedAt}:${studentID}`. Kept apart
 * from the release rows on purpose: a re-release deletes and rewrites those, so they cannot remember whom a mail
 * already reached. (Older documents keyed `${examId}:${studentID}` stem from when only the first release mailed.)
 */
export const NOTIFICATIONS_COLLECTION = 'resultNotifications';
const DUPLICATE_KEY = 11000;

/**
 * Mails each given student that the results of an exam were released to them, if they switched on
 * "E-Mail Benachrichtigungen" in their settings. The link opens the result in the app (see
 * client/lib/pending-result-link.ts). Sent to the account's login address, which sign-up has confirmed.
 *
 * Every release mails the students it is given, re-releases included; the caller passes only those whose result is
 * new or changed (see `releaseExamResultsMutation`). The record is claimed per release BEFORE sending, so the same release cannot mail a student
 * twice, and dropped again if the mail fails. Students who are not opted in get no record at all.
 *
 * Never throws: the release has already happened, a failed mail must not turn it into an error.
 */
export async function notifyStudentsOfReleasedResults({
  examId,
  classRoomID,
  examTitle,
  examSubject,
  className,
  releasedAt,
  studentIDs,
}: {
  examId: string;
  classRoomID: string;
  /** The release's shared timestamp — what tells one release's mails apart from the next one's. */
  releasedAt: string;
  examTitle?: string | null;
  examSubject?: string | null;
  className?: string | null;
  studentIDs: string[];
}) {
  try {
    if (!studentIDs.length) return;

    const students = await (await getRawCollection(Student._collectionName))
      .find(
        { _id: { $in: studentIDs as any[] }, emailNotifications: true },
        { projection: { firstName: 1, userID: 1 } },
      )
      .toArray();
    if (!students.length) return;

    const accounts = await (await getRawCollection(AdornisUser._collectionName))
      .find({ _id: { $in: students.map(s => s.userID) as any[] } }, { projection: { email: 1 } })
      .toArray();
    const emailByUserID = new Map(accounts.map(account => [String(account._id), account.email]));

    const subjectLabel = subjectLabelDe(examSubject);

    const notifications = await getRawCollection(NOTIFICATIONS_COLLECTION);
    let sent = 0;
    for (const student of students) {
      const email = emailByUserID.get(String(student.userID));
      if (typeof email !== 'string' || !email) continue;

      // `for` names the student account the result belongs to, so the app can tell when another account (a
      // teacher's, a sibling's on a shared computer) is signed in instead of quietly opening the wrong one.
      const resultsLink = new URL('/', baseConfig.get('ROOT_URL'));
      resultsLink.searchParams.set('results', examId);
      resultsLink.searchParams.set('class', classRoomID);
      resultsLink.searchParams.set('for', String(student.userID));

      const notificationID = `${examId}:${releasedAt}:${String(student._id)}`;
      try {
        await notifications.insertOne({
          _id: notificationID as any,
          examId,
          releasedAt,
          studentID: String(student._id),
          claimedAt: new Date(),
        });
      } catch (err: any) {
        if (err?.code === DUPLICATE_KEY) continue; // already mailed for this release (or being mailed right now)
        logger.error(
          { err, examId, studentID: String(student._id), context: 'RESULT_NOTIFICATIONS' },
          'Mail-Vermerk fehlgeschlagen',
        );
        continue;
      }

      try {
        await sendExamResultsEmail(
          email,
          {
            firstName: typeof student.firstName === 'string' ? student.firstName : undefined,
            examTitle: examTitle ?? undefined,
            subjectLabel,
            className: className ?? undefined,
            resultsLink: resultsLink.toString(),
          },
          String(student.userID),
        );
      } catch {
        // Logged with the mail context in sendMail. The claim is released so the record only ever means "mailed";
        // the other students still get theirs.
        await notifications.deleteOne({ _id: notificationID as any }).catch(() => undefined);
        continue;
      }
      sent += 1;
      // Outside the try above: the mail is out, so a failed bookkeeping write must not release the claim. The claim
      // alone already blocks a second mail for this release.
      await notifications
        .updateOne({ _id: notificationID as any }, { $set: { sentAt: new Date() } })
        .catch(() => undefined);
    }
    logger.info(
      { examId, notified: sent, optedIn: students.length, context: 'RESULT_NOTIFICATIONS' },
      'Ergebnis-Mails versendet',
    );
  } catch (err) {
    logger.error({ err, examId, context: 'RESULT_NOTIFICATIONS' }, 'Ergebnis-Mails fehlgeschlagen');
  }
}
